import { useAnalysisPDFStyles } from "@hooks/useAnalysisPDFStyle";
import { FeeSubProductDTO } from "@models/platform-analysis/entities/steps/fee";
import { FeesDisplayStyle } from "@models/platform-analysis/enums/fee/displayStyle";
import { FeesPerformanceScope } from "@models/platform-analysis/enums/fee/feesPerformanceScope";
import { FeeHeatmapMode } from "@models/platform-analysis/enums/fee/heatmapMode";
import { useTheme } from "@mui/material";
import {
  HeatMapTableTitleConstants,
  LineChartLegendConstants,
} from "@pages/platform-analysis-v2/_id/steps/fee/analysis/constants";
import {
  displayCurrencyNumber,
  getFeeDetailMinMaxExcludePerformanceFees,
  getFeeMinMaxExcludePerformanceFees,
  removeSubProductNameForSpecificProduct,
} from "@pages/platform-analysis/util";
import { Text, View } from "@react-pdf/renderer";
import {
  formatPercentage,
  getHSLLightness,
  hslToHex,
  thousandSeparator,
} from "@utils";
import { defaultTo, isNull } from "lodash";
import { PDFLabelLineDetail } from "../../../export-pdf/components/heat-map/label-line-detail";
import { PDFLabelLineProjection } from "../../../export-pdf/components/heat-map/label-line-projection";
import { PDFVerticalDashLineDetail } from "../../../export-pdf/components/heat-map/vertical-dash-line-detail";
import { PDFVerticalDashLineProjection } from "../../../export-pdf/components/heat-map/vertical-dash-line-projection";
import { HorizontalLine } from "../../../export-pdf/components/horizontal-line";
import { PDFFeeSubProductCard } from "../../../export-pdf/components/sub-product-card";
import { PDFStyleConfigs } from "../../../export-pdf/configs";
import { calculateFeeDetailColumnWidth } from "../../../export-pdf/utils";

export interface PDFFeeAnalysisSectionProps {
  totalSubProductHidden: number;
  subProducts: FeeSubProductDTO[];
  totalPortfolioValue?: number;
  mode?: FeesDisplayStyle;
  heatmapMode?: FeeHeatmapMode;
  color?: {
    hue: number;
    saturation: number;
  };
  minLightness?: number;
  maxLightness?: number;
  avgLightness?: number;
  captions?: {
    dollar: string;
    percentage: string;
  };
  isContinue?: boolean;
  showFeeAnalysisText?: boolean;
  showFeeEstimatesText?: boolean;
  showAnnualPlatformFeesText?: boolean;
  isFamilyLevel?: boolean;
  feesPerformanceScope?: FeesPerformanceScope;
  isExcludePerformanceFees?: boolean;
  showRecommendationLabel: boolean;
  title?: string;
  isAppendix?: boolean;
}

export const PDFFeeAnalysisHeatMap = ({
  totalSubProductHidden,
  subProducts,
  totalPortfolioValue,
  mode = FeesDisplayStyle.Dollar,
  heatmapMode = FeeHeatmapMode.Projection,
  color = {
    hue: PDFStyleConfigs.FeeAnalysisDefaultHue,
    saturation: PDFStyleConfigs.FeeAnalysisDefaultSaturation,
  },
  minLightness = PDFStyleConfigs.FeeAnalysisMinLightness,
  maxLightness = PDFStyleConfigs.FeeAnalysisMaxLightness,
  avgLightness = PDFStyleConfigs.FeeAnalysisAvgLightness,
  captions = {
    dollar: "Total fees",
    percentage: "of total portfolio value",
  },
  showFeeAnalysisText = false,
  showFeeEstimatesText = false,
  isFamilyLevel,
  feesPerformanceScope,
  isExcludePerformanceFees = false,
  showRecommendationLabel,
  title,
  isAppendix,
}: PDFFeeAnalysisSectionProps) => {
  const { palette } = useTheme();
  const { typographyStyle } = useAnalysisPDFStyles();
  const isDollarMode = mode === FeesDisplayStyle.Dollar;
  const isProjectionMode = heatmapMode === FeeHeatmapMode.Projection;
  const isIncludePerf = feesPerformanceScope === FeesPerformanceScope.Including;
  const hasTooManyFeeColumns = subProducts.some(
    (subProduct) =>
      subProduct.feeDetails.length >
      PDFStyleConfigs.FeeAnalysisMaxFeeDetailsColumnToDisplayLogo,
  );
  const contentWidth = hasTooManyFeeColumns
    ? PDFStyleConfigs.FeeAnalysisFeeDetailContentWidthWithoutLogo
    : PDFStyleConfigs.FeeAnalysisFeeDetailContentWidth;

  const removedSubProductNameForSpecificProduct =
    removeSubProductNameForSpecificProduct(subProducts ?? []);
  const showWarningCaptions = subProducts?.some(
    (subProduct: FeeSubProductDTO) => subProduct.warning,
  );

  const renderTitle = () => {
    let mainTitle = "";

    if (!isFamilyLevel) {
      mainTitle = HeatMapTableTitleConstants.DefaultTitle;
    } else {
      const isProjection = heatmapMode === FeeHeatmapMode.Projection;
      if (isProjection) {
        mainTitle = isIncludePerf
          ? HeatMapTableTitleConstants.ProjectionViewTitle
          : HeatMapTableTitleConstants.ProjectionViewExcPerfTitle;
      } else {
        mainTitle = isIncludePerf
          ? HeatMapTableTitleConstants.DetailViewTitle
          : HeatMapTableTitleConstants.DetailViewExcPerfTitle;
      }
    }

    const isDetailView = heatmapMode === FeeHeatmapMode.Detail;

    return `${mainTitle}: ${isDetailView ? "Detail view" : "Projection"}`;
  };

  const renderProjection = () => {
    const firstProductFees = subProducts[0]?.fees ?? [];
    const { min, max } = getFeeMinMaxExcludePerformanceFees({
      subProducts,
      isDollarMode,
      isExcludePerformanceFees,
    });

    return (
      <View style={{ width: PDFStyleConfigs.FeeAnalysisFeeDetailContentWidth }}>
        <PDFLabelLineProjection firstProductFees={firstProductFees} />
        <PDFVerticalDashLineProjection colNumber={firstProductFees?.length} />
        <View style={{ flexDirection: "row" }}>
          <View style={{ justifyContent: "space-between" }}>
            {Array.from(Array(subProducts.length + 1).keys()).map((index) => (
              <View
                key={index}
                style={{
                  width: 3,
                  height: 0.5,
                  backgroundColor: palette.text.primary,
                  transform:
                    index === 0
                      ? "translateY(1.5px)"
                      : index === subProducts.length
                      ? "translateY(-1.5px)"
                      : undefined,
                }}
              />
            ))}
          </View>
          <View
            style={{
              gap: 1,
            }}
          >
            {subProducts.map((subProduct, subProductIndex) => (
              <View key={subProductIndex} style={{ flexDirection: "row" }}>
                {subProduct.fees.map((fee, index) => {
                  const displayDollarValue = isExcludePerformanceFees
                    ? defaultTo(
                        fee.totalCostExcPerfForDisplay,
                        fee.totalCostForDisplay,
                      )
                    : fee.totalCostForDisplay;
                  const displayPercentageValue = isExcludePerformanceFees
                    ? defaultTo(
                        fee.totalCostExcPerfForDisplayPercentage,
                        fee.totalCostForDisplayPercentage,
                      )
                    : fee.totalCostForDisplayPercentage;

                  const lightness = getHSLLightness(
                    min,
                    max,
                    isDollarMode ? displayDollarValue : displayPercentageValue,
                    minLightness,
                    maxLightness,
                  );
                  const isDarkCell = lightness < avgLightness;
                  const displayData = isDollarMode
                    ? `$${thousandSeparator(+displayDollarValue.toFixed(0))}`
                    : `${formatPercentage(displayPercentageValue)}%`;

                  return (
                    <View
                      style={{
                        flexDirection: "column",
                        justifyContent: "center",
                        alignContent: "center",
                        height: 30,
                        width:
                          PDFStyleConfigs.FeeAnalysisFeeDetailContentWidth /
                          subProduct?.fees?.length,
                        backgroundColor: hslToHex(
                          color.hue,
                          color.saturation,
                          lightness,
                        ),
                        margin: "1.5px",
                      }}
                      key={index}
                    >
                      <Text
                        style={{
                          ...typographyStyle?.body2,
                          color: isDarkCell
                            ? palette.common.white
                            : palette.text.darker,
                          textAlign: "center",
                          fontSize: 7,
                        }}
                      >
                        {displayData}
                      </Text>
                    </View>
                  );
                })}
              </View>
            ))}
          </View>
        </View>

        <PDFVerticalDashLineProjection colNumber={firstProductFees?.length} />
        <PDFLabelLineProjection firstProductFees={firstProductFees} />

        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: showWarningCaptions ? "flex-start" : "center",
            marginTop: 10,
          }}
        >
          <View style={{ gap: 7 }}>
            <View
              style={{
                flexDirection: "row",
                gap: 2,
                marginTop: "2px",
              }}
            >
              <Text style={{ ...typographyStyle?.body2 }}>
                {isDollarMode ? " $" : "%"}
              </Text>
              <Text style={{ ...typographyStyle?.body2, fontWeight: "bold" }}>
                {isDollarMode ? captions.dollar : captions.percentage}
              </Text>
            </View>

            {showWarningCaptions && (
              <View
                style={{
                  flexDirection: "row",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    color: palette.error.main,
                  }}
                >
                  * Product is missing some investment options, see web version
                  of this report for full details
                </Text>
              </View>
            )}
          </View>
          <View
            style={{
              backgroundColor: "#e0e2e5",
              width: 180,
              minWidth: 180,
              height: 10,
              alignItems: "center",
              justifyContent: "center",
              border: 0.5,
              borderColor: "#b1b6bf",
            }}
          >
            <Text style={{ ...typographyStyle.body2 }}>
              {LineChartLegendConstants.XAxis}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const renderDetails = () => {
    if (!subProducts[0]?.feeDetails) return null;
    const firstProductFeeDetails = subProducts[0]?.feeDetails ?? [];
    const arrayMinMax = getFeeDetailMinMaxExcludePerformanceFees({
      subProducts,
      isDollarMode,
      isExcludePerformanceFees,
    });

    const { pysColumnWidth, otherColumnWidth } = calculateFeeDetailColumnWidth(
      contentWidth,
      firstProductFeeDetails,
    );

    return (
      <View style={{ width: contentWidth }}>
        <PDFLabelLineDetail
          firstProductFeeDetails={firstProductFeeDetails}
          hasTooManyFeeColumns={hasTooManyFeeColumns}
          isExcludePerformanceFees={isExcludePerformanceFees}
        />
        <PDFVerticalDashLineDetail
          firstProductFeeDetails={firstProductFeeDetails}
          hasTooManyFeeColumns={hasTooManyFeeColumns}
        />

        <View style={{ flexDirection: "row" }}>
          <View style={{ justifyContent: "space-between" }}>
            {Array.from(Array(subProducts.length + 1).keys()).map((index) => (
              <View
                key={index}
                style={{
                  width: 3,
                  height: 0.5,
                  backgroundColor: palette.text.primary,
                  transform:
                    index === 0
                      ? "translateY(1.5px)"
                      : index === subProducts.length
                      ? "translateY(-1.5px)"
                      : undefined,
                }}
              />
            ))}
          </View>
          <View
            style={{
              gap: 1,
            }}
          >
            {subProducts.map((subProduct, subProductIndex) => (
              <View key={subProductIndex} style={{ flexDirection: "row" }}>
                {subProduct.feeDetails.map((feeDetail, index) => {
                  const { min, max } = arrayMinMax[index] ?? {
                    min: 0,
                    max: 0,
                  };
                  const dollarValueToRender = !isExcludePerformanceFees
                    ? feeDetail.dollar
                    : defaultTo(feeDetail.dollarExcPerf, feeDetail.dollar);
                  const percentageValueToRender = !isExcludePerformanceFees
                    ? feeDetail.percentage
                    : defaultTo(
                        feeDetail.percentageExcPerf,
                        feeDetail.percentage,
                      );
                  const lightness = getHSLLightness(
                    min,
                    max,
                    isDollarMode
                      ? dollarValueToRender || 0
                      : percentageValueToRender || 0,
                    minLightness,
                    maxLightness,
                  );
                  const isDarkCell = lightness < avgLightness;

                  const displayData =
                    isNull(percentageValueToRender) ||
                    isNull(dollarValueToRender)
                      ? "No data"
                      : isDollarMode
                      ? `$${thousandSeparator(+dollarValueToRender.toFixed(0))}`
                      : `${formatPercentage(percentageValueToRender)}%`;

                  return (
                    <View
                      style={{
                        flexDirection: "column",
                        justifyContent: "center",
                        alignContent: "center",
                        height: 30,
                        width: feeDetail.isPysRefund
                          ? pysColumnWidth
                          : otherColumnWidth,
                        backgroundColor: hslToHex(
                          color.hue,
                          color.saturation,
                          lightness,
                        ),
                        marginVertical: "1.5px",
                        marginRight: index === 0 ? "5px" : "1.5px",
                        marginLeft:
                          index === subProduct.feeDetails.length - 1
                            ? "5px"
                            : "1.5px",
                      }}
                      key={index}
                    >
                      <Text
                        style={{
                          ...typographyStyle?.body2,
                          color: isDarkCell
                            ? palette.common.white
                            : palette.text.darker,
                          textAlign: "center",
                          fontSize: 7,
                        }}
                      >
                        {displayData}
                      </Text>
                    </View>
                  );
                })}
              </View>
            ))}
          </View>
        </View>

        <PDFVerticalDashLineDetail
          firstProductFeeDetails={firstProductFeeDetails}
          hasTooManyFeeColumns={hasTooManyFeeColumns}
        />
        <PDFLabelLineDetail
          firstProductFeeDetails={firstProductFeeDetails}
          hasTooManyFeeColumns={hasTooManyFeeColumns}
          isExcludePerformanceFees={isExcludePerformanceFees}
        />

        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: showWarningCaptions ? "flex-start" : "center",
            marginTop: 10,
          }}
        >
          <View style={{ gap: 7 }}>
            <View
              style={{
                flexDirection: "row",
                gap: 2,
                marginTop: "2px",
              }}
            >
              <Text style={{ ...typographyStyle?.body2 }}>
                {isDollarMode ? " $" : "%"}
              </Text>
              <Text style={{ ...typographyStyle?.body2, fontWeight: "bold" }}>
                {`Fees for a total balance of ${displayCurrencyNumber(
                  totalPortfolioValue ?? 0,
                )}`}
              </Text>
            </View>

            {showWarningCaptions && (
              <View
                style={{
                  flexDirection: "row",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    color: palette.error.main,
                  }}
                >
                  * Product is missing some investment options, see web version
                  of this report for full details
                </Text>
              </View>
            )}
          </View>
          <View
            style={{
              backgroundColor: "#e0e2e5",
              width: 60,
              minWidth: 60,
              height: 10,
              alignItems: "center",
              justifyContent: "center",
              border: 0.5,
              borderColor: "#b1b6bf",
            }}
          >
            <Text style={{ ...typographyStyle.body2 }}>Fee Type</Text>
          </View>
        </View>
      </View>
    );
  };

  const hasData =
    subProducts.length &&
    (isProjectionMode
      ? subProducts[0].fees?.length
      : subProducts[0].feeDetails?.length);

  let titleHeight = PDFStyleConfigs.TitleHeight;
  if (showFeeAnalysisText) {
    titleHeight += PDFStyleConfigs.TitleHeight;
    if (!isProjectionMode && !showFeeEstimatesText) {
      titleHeight -= 15;
    }
  }

  if (isProjectionMode)
    titleHeight -=
      PDFStyleConfigs.FeeAnalysisReduceFeeColumnHeight *
      (isFamilyLevel ? -1 : 1);

  if (subProducts[0]?.feeDetails.length > 4 && !isProjectionMode)
    titleHeight += 5;

  return (
    <View
      style={{
        gap: 25,
        width: "100%",
      }}
    >
      <View
        style={{
          gap: `${showFeeEstimatesText} ? "5px" : "0px"`,
          height: titleHeight,
        }}
      >
        {showFeeAnalysisText && (
          <View>
            <Text
              style={{
                ...typographyStyle.title,
                color: palette.common.black,
              }}
            >
              {`${title} (continued)` || "Fee analysis (continued)"}
            </Text>
            <HorizontalLine color={palette.common.black} />
          </View>
        )}
        {showFeeEstimatesText && (
          <Text
            style={{
              ...typographyStyle.subtitle,
              color: palette.common.black,
              marginBottom: "2px",
            }}
          >
            {`Fee estimates`}
            {isAppendix && (
              <Text style={{ fontWeight: "normal" }}>
                {
                  " of platforms that made it to the fee review stage but were not selected"
                }
              </Text>
            )}
          </Text>
        )}

        <Text
          style={{
            ...typographyStyle.subtitle,
            color: palette.common.black,
          }}
        >
          {renderTitle()}
        </Text>
      </View>

      {hasData ? (
        <View
          style={{
            gap: 5,
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "flex-start",
          }}
        >
          <View
            style={{
              gap: 4,
              flexDirection: "column",
              marginTop: isProjectionMode ? 13 : 30,
            }}
          >
            {removedSubProductNameForSpecificProduct.map(
              (subProduct, subProductIndex) => (
                <View
                  key={subProductIndex}
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 5,
                    height: 30,
                  }}
                >
                  <PDFFeeSubProductCard
                    showRecommendationLabel={showRecommendationLabel}
                    subProduct={subProduct}
                  />
                </View>
              ),
            )}
            <View
              style={{
                height: 10,
                alignItems: "center",
                justifyContent: "flex-start",
                marginTop: !isProjectionMode ? "37px" : "20px",
              }}
            >
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: "#1E1970",
                  fontWeight: "bold",
                }}
              >
                {totalSubProductHidden > 1
                  ? `${totalSubProductHidden} products hidden`
                  : totalSubProductHidden === 1
                  ? `${totalSubProductHidden} product hidden`
                  : ""}
              </Text>
            </View>
          </View>
          {isProjectionMode ? renderProjection() : renderDetails()}
        </View>
      ) : (
        <View style={{ gap: "5px" }}>
          <Text
            style={{
              ...typographyStyle.body1,
              fontWeight: "bold",
              color: "#b1b6bf",
            }}
          >
            No data
          </Text>
          <Text style={{ ...typographyStyle.body1, color: "#b1b6bf" }}>
            No products match the criteria in your filter
          </Text>
          <Text
            style={{
              ...typographyStyle.body2,
              color: "#1E1970",
              fontWeight: "bold",
              marginTop: "25px",
            }}
          >
            {totalSubProductHidden > 1
              ? `${totalSubProductHidden} products hidden`
              : totalSubProductHidden === 1
              ? `${totalSubProductHidden} product hidden`
              : ""}
          </Text>
        </View>
      )}
    </View>
  );
};
